import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { Modal } from "../components/base/Modal";
import { Button } from "../components/base/Button";

export interface ConfirmOptions {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  dangerText?: ReactNode;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface Pending {
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
      setPending({ options, resolve });
    });
  }, []);

  const close = useCallback((result: boolean) => {
    resolver.current?.(result);
    resolver.current = null;
    setPending(null);
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  const isDestructive = pending?.options.destructive ?? false;

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Modal
        open={Boolean(pending)}
        onClose={() => close(false)}
        title={pending?.options.title ?? "Are you sure?"}
        size="sm"
        hideCloseButton
      >
        {pending ? (
          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <div
                className={`flex h-10 w-10 flex-none items-center justify-center rounded-full ${
                  isDestructive ? "bg-danger-100 text-danger-600" : "bg-secondary-100 text-secondary-700"
                }`}
              >
                <i className={`${isDestructive ? "ri-alert-line" : "ri-question-line"} text-xl`} />
              </div>
              <div className="pt-0.5 text-sm text-foreground-700">{pending.options.message}</div>
            </div>
            {pending.options.dangerText ? (
              <div className="rounded-md bg-danger-50 px-3 py-2 text-xs text-danger-700">
                {pending.options.dangerText}
              </div>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => close(false)}>
                {pending.options.cancelLabel ?? "Cancel"}
              </Button>
              <Button
                variant={isDestructive ? "danger" : "primary"}
                onClick={() => close(true)}
              >
                {pending.options.confirmLabel ?? (isDestructive ? "Delete" : "Confirm")}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}